export const INSIGHT_SHARE_TYPE_BLACK_LIST: Record<string, true> = {
  personal: true,
  // anonymous: true,
  inside: true,
  outside: true,
  informationRisk: true,
};

export type InsightShareTypes =
  | "Shared to untrusted employee"
  | "Shared to untrusted organization"
  | "Shared to untrusted personal address"
  | "Shared to untrusted folder"
  | "Insecurely shared by untrusted user";

export type EventLabelDir =
  | InsightShareTypes
  | "inside"
  | "outside"
  | "personal"
  | "anonymous"
  | "informationRisk";

export type AppsInsightSeverityLevel = "None" | "Low" | "Medium" | "High";

export type AppNoneGenericInsightRiskType =
  | "overExposure"
  | "overSharing"
  | "unsecureSharing"
  | "lackOfLabeling"
  | "lackOfProtection"
  | "unprotectedAccounts";

export type AppInsightRiskType =
  | AppNoneGenericInsightRiskType
  | "overall"
  | "generic"
  | "internalInformationExposed"
  | "externalInformationExposed";

export const AppInsightRiskTypeHelper = {
  as_dropdown_items: () =>
    ["overExposure", "overSharing", "unsecureSharing", "lackOfLabeling"].map(
      (id) => {
        return {
          id,
          check: false,
          name: AppInsightRiskTypeHelper.asString(id as AppInsightRiskType),
        };
      }
    ),
  asString: (id: AppInsightRiskType) => {
    switch (id) {
      case "generic":
        return "generic";
      case "overExposure":
        return "over exposure";
      case "overSharing":
        return "over sharing";
      case "unsecureSharing":
        return "unsecure sharing";
      case "lackOfProtection":
        return "lack of protection";
      case "unprotectedAccounts":
        return "unprotected accounts";
      case "lackOfLabeling":
      default:
        return "lack of labeling";
    }
  },
};

export interface AppInsightExtraProps {
  anomaly: boolean;
  sensitivity?: 0 | 1 | 2 | 3;
  shareType: EventLabelDir;
  infoTypeId?: string;
  highlyCritical: boolean;
  riskType: AppInsightRiskType;
}

export interface ApiInsight {
  id: string;
  enabled: boolean;
  type: "Detection" | "Vulnerability";
  name: string;
  severity: AppsInsightSeverityLevel;
  extraProperties?: AppInsightExtraProps;
}

export interface Insight {
  id: string;
  enabled: boolean;
  name: string;
  type: "Detection" | "Vulnerability" | "Compliance";
  severity: AppsInsightSeverityLevel; // None Low Medium High
  severityLevel: number;
  sensitivity: 0 | 1 | 2 | 3;
  sensitivityStr: string;
  shareType: EventLabelDir;
  shareTypeStr: string;
  shareTypeHtml: string;
  infoTypeId: string;
  eventLabel: EventLabelDir;
  isAnomaly: boolean;
  description: string;
  exposedByCount: number;
  exposedToCount: number;
  eventsCount: number;
  highlyCritical: boolean;
  riskType: AppInsightRiskType;
}

export const DEFAULT_EXTRA_PROPERTIES: AppInsightExtraProps = {
  anomaly: false,
  sensitivity: 0,
  shareType: "inside",
  infoTypeId: "",
  highlyCritical: false,
  riskType: "generic",
};

/* 
[
    "Shared to untrusted employee",
    "Shared to untrusted organization",
    "Shared to untrusted personal address"
]

*/

const shareTypeToString = (shareType: EventLabelDir) => {
  switch (shareType) {
    case "anonymous":
      return "Shared via anonymous link";
    case "inside":
      return "Shared inside the organization";
    case "outside":
      return "Shared outside the organization";
    case "personal":
      return "Shared to a private email address";
    default:
      return shareType;
  }
};

const shareTypeToHtmlString = (shareType: EventLabelDir) => {
  switch (shareType) {
    case "anonymous":
      return "Shared via <b>anonymous link</b>";
    case "Shared to untrusted employee":
      return "Shared to untrusted <b>employee</b>";
    case "Shared to untrusted organization":
      return "Shared to untrusted <b>organization</b>";
    case "Shared to untrusted personal address":
      return "Shared to untrusted <b>personal address</b>";
    case "inside":
      return "Shared <b>inside</b> the <b>organization</b>";
    case "outside":
      return "Shared <b>outside</b> the <b>organization</b>";
    case "personal":
      return "Shared to a <b>private email address</b>";
    default:
      return shareType;
  }
};

const sensitivityToString = (sensitivity: 0 | 1 | 2 | 3) => {
  return [
    "General content",
    "Low sensitivity content",
    "Medium sensitivity content",
    "Highly sensitive content",
  ][sensitivity];
};

const fromExtendedProperties = (insight: ApiInsight) => {
  const copy = { ...insight };
  if (!copy.extraProperties) {
    // @ts-ignore
    copy.extraProperties = {
      ...DEFAULT_EXTRA_PROPERTIES,
    };
  }
  return (key: string) => {
    // @ts-ignore
    if (key in copy.extraProperties) {
      // @ts-ignore
      return copy.extraProperties[key];
    } else {
      // @ts-ignore
      return DEFAULT_EXTRA_PROPERTIES[key];
    }
  };
};

export const InsightHelper = {
  toSeverityNum: (severity: AppsInsightSeverityLevel): number => {
    return ["None", "Low", "Medium", "High"].indexOf(severity);
  },
  fromApiInsight: (insight: ApiInsight): Insight => {
    const toProp = fromExtendedProperties(insight);
    const shareType = toProp("shareType");
    const sensitivity = toProp("sensitivity");
    return {
      id: insight.id,
      type: insight.type,
      enabled: insight.enabled,
      name: insight.name,
      // @ts-ignore
      severity: insight.severity,
      severityLevel: ["None", "Low", "Medium", "High"].indexOf(
        insight.severity
      ),
      // @ts-ignore
      sensitivity,
      sensitivityStr: sensitivityToString(sensitivity),
      // @ts-ignore
      shareType,
      shareTypeStr: shareTypeToString(shareType),
      shareTypeHtml: shareTypeToHtmlString(shareType),
      // @ts-ignore
      infoTypeId: toProp("infoTypeId"),
      // @ts-ignore
      isAnomaly: toProp("anomaly"),
      eventLabel: toProp("shareType"),
      description: "",
      exposedByCount: 0,
      exposedToCount: 0,
      eventsCount: 0,
      highlyCritical: toProp("highlyCritical") || false,
      riskType: toProp("riskType") || "generic",
    };
  },
};
